// The class's recordings, for its instructor (byj-1wv.51.6): every recording
// a student made of an assignment, newest first, grouped by assignment — each
// heading saying how many on it have not recorded it yet — or by student, a
// search narrowing either to the students it names. An instructor sees a
// recording only once it is uploaded and set to one of the class's
// assignments (RFC-043 "Recordings"); the relay lists them, with those
// counts, in one request (byj-j98), read once as the page opens so its tab
// says how many there are before it is opened.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, type Assignment, type AssignmentRecording, type ClassDetail, type ClassRecording, type ClassRecordingList } from "../api";
import { useKeptChoice } from "../kept";
import { t, when, whenShort, phrases } from "../i18n";
import { ListSearch } from "./ListSearch";
import { Link } from "./Nav";
import { Grouped, groupsBy, type Group } from "./Rows";
import { RecordingTags } from "./RecordingRow";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

/** A recording as a row: its title the link to its review, what it holds under it, and whether it kept its screen, as /shares' rows say. */
export function RecordingLine({ rec, sub }: { rec: AssignmentRecording; sub?: ReactNode }) {
  return (
    <li className="share-row rec-row is-ended no-menu">
      <span className="row-lead" />
      <span className="kind-box" aria-hidden="true">
        ●
      </span>
      <div className="row-main">
        {/* the page's own route, not review_url: that is the relay's public address, which is not this page's where they differ (a dev server) */}
        <Link to={`/r/${encodeURIComponent(rec.id)}`} className="row-title">
          {rec.title}
        </Link>
        <div className="row-sub">{sub ?? phrases.recordingTurns(rec.turns[0], rec.turns[1])}</div>
      </div>
      {/* the time's title, for a screen with no pointer to hover, as /shares' rows have it */}
      <div className="row-detail">{t("recordings:recordingRecorded", { when: when(rec.created_at) })}</div>
      <div className="row-meta">
        <RecordingTags screen={rec.screen} score={rec.score} uploaded={rec.uploaded} />
        <time className="row-time" dateTime={rec.created_at} title={when(rec.created_at)}>
          {whenShort(rec.created_at)}
        </time>
      </div>
    </li>
  );
}

type RecordingsBy = "assignment" | "student";
const bys: RecordingsBy[] = ["assignment", "student"];

export function ClassRecordings({ cls, onCount }: {
  cls: ClassDetail;
  /** how many recordings it found, for its tab's count */
  onCount?: (n: number) => void;
}) {
  const [data, setData] = useState<ClassRecordingList | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [by, setBy] = useKeptChoice<RecordingsBy>("class-manage.recordings-by", "assignment", bys);

  // the class's, and any a recording is of that was added since the page
  // read them, so every recording has its group; open ones first, as the
  // Assignments tab lists them
  const assignments = useMemo(() => {
    const all: Pick<Assignment, "key" | "title" | "archived_at">[] = [...(cls.assignments ?? [])];
    for (const x of data?.recordings ?? []) if (!all.some((a) => a.key === x.assignment.key)) all.push(x.assignment);
    return [...all.filter((a) => !a.archived_at), ...all.filter((a) => a.archived_at)];
  }, [cls.assignments, data]);

  const read = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.classRecordings(cls.id));
      setFailed(null);
    } catch (err) {
      setFailed(errText(err));
    }
    setLoading(false);
  }, [cls.id]);
  // once: an assignment edited or archived on the page reads nothing again; Refresh does
  const [asked, setAsked] = useState(false);
  useEffect(() => {
    if (!asked) {
      setAsked(true);
      void read();
    }
  }, [asked, read]);

  const items = useMemo(() => data?.recordings ?? [], [data]);
  useEffect(() => {
    if (data) onCount?.(items.length);
  }, [data, items.length, onCount]);

  const q = query.trim().toLowerCase();
  const shown = q ? items.filter((x) => x.student.name.toLowerCase().includes(q)) : items;
  const students = new Set(items.map((x) => x.student.id)).size;

  const groups: Group<ClassRecording>[] =
    by === "assignment"
      ? assignments.map((a) => {
          const missing = data?.not_recorded[a.key] ?? 0;
          return {
            key: a.key,
            title: a.archived_at ? `${a.title} (${t("classes:archived")})` : a.title,
            items: shown.filter((x) => x.assignment.key === a.key),
            actions: missing > 0 ? <span className="muted small">{t("classes:notRecordedN", { count: missing })}</span> : undefined,
          };
        })
      : groupsBy(shown, (x) => ({ key: x.student.id, title: x.student.name })).sort((a, b) => a.title.localeCompare(b.title));

  // any failure, an error with no message too (null is none); the tab's
  // count stays unsaid until a read says it
  const failure = failed !== null && (
    <p className="error">
      {t("common:error", { m: failed })}{" "}
      <button className="small" disabled={loading} onClick={() => void read()}>
        {t("common:retry")}
      </button>
    </p>
  );
  if (!data) return failure || <p className="muted">{t("common:loading")}</p>;
  return (
    <>
      {failure}
      {items.length === 0 ? (
        <div className="empty-card" data-testid="no-recordings">
          <h3 className="flush">{t("classes:noClassRecordings")}</h3>
          <p className="muted small">{t("classes:classRecordingsHelp")}</p>
        </div>
      ) : (
        <>
          <p className="muted small roster-summary" data-testid="recordings-summary">
            {phrases.classRecordingsSummary(items.length, students)}
          </p>
          <div className="recordings-tools">
            <ListSearch value={query} onChange={setQuery} label={t("classes:searchStudents")} shown={shown.length} of={items.length} />
            <div className="filter-chips row" role="group" aria-label={t("classes:groupRecordingsBy")}>
              {bys.map((b) => (
                <button key={b} type="button" className={`chip filter-chip${by === b ? " picked" : ""}`} aria-pressed={by === b} onClick={() => setBy(b)}>
                  {b === "assignment" ? t("classes:byAssignment") : t("classes:byStudent")}
                </button>
              ))}
            </div>
            <button type="button" disabled={loading} onClick={() => void read()}>
              {loading ? t("common:loading") : t("common:refresh")}
            </button>
          </div>
          {shown.length === 0 ? (
            <p className="muted">{t("classes:noRecordingMatch")}</p>
          ) : (
            <Grouped
              id="recordings"
              headed
              groups={groups}
              row={(x) => (
                <RecordingLine
                  key={x.id}
                  rec={x}
                  sub={
                    <>
                      {by === "assignment" ? x.student.name : x.assignment.title} · {phrases.recordingTurns(x.turns[0], x.turns[1])}
                    </>
                  }
                />
              )}
            />
          )}
        </>
      )}
    </>
  );
}
